import { createTheme } from '@mui/material/styles'

const lightPalette = {
  mode: 'light',
  primary: { main: '#0d5b4d', light: '#14857a', dark: '#083d32', contrastText: '#ffffff' },
  secondary: { main: '#8b5cf6', light: '#a78bfa', dark: '#6d28d9' },
  success: { main: '#059669', light: '#d1fae5', dark: '#065f46' },
  error: { main: '#dc2626', light: '#fee2e2', dark: '#991b1b' },
  warning: { main: '#d97706', light: '#fef3c7', dark: '#92400e' },
  info: { main: '#0284c7', light: '#dbeafe', dark: '#075985' },
  background: { default: '#fafaf7', paper: '#ffffff' },
  text: { primary: '#171717', secondary: '#525252', disabled: '#a3a3a3' },
  divider: '#e5e5e0',
  grey: {
    50: '#fafaf7', 100: '#f5f5f0', 200: '#e5e5e0', 300: '#d4d4cf',
    400: '#a3a3a0', 500: '#737373', 600: '#525252', 700: '#404040',
    800: '#262626', 900: '#171717',
  },
}

const darkPalette = {
  mode: 'dark',
  primary: { main: '#14b8a6', light: '#0d2e2a', dark: '#2dd4bf', contrastText: '#ffffff' },
  secondary: { main: '#a78bfa', light: '#1e1b4b', dark: '#c4b5fd' },
  success: { main: '#10b981', light: '#052e16', dark: '#6ee7b7' },
  error: { main: '#ef4444', light: '#450a0a', dark: '#fca5a5' },
  warning: { main: '#f59e0b', light: '#3d1f00', dark: '#fcd34d' },
  info: { main: '#38bdf8', light: '#0c1a2e', dark: '#7dd3fc' },
  background: { default: '#111110', paper: '#1c1c1a' },
  text: { primary: '#fafaf7', secondary: '#a3a3a0', disabled: '#525252' },
  divider: '#2a2a27',
  grey: {
    50: '#111110', 100: '#1c1c1a', 200: '#2a2a27', 300: '#3d3d3a',
    400: '#525250', 500: '#737370', 600: '#a3a3a0', 700: '#d4d4cf',
    800: '#e5e5e0', 900: '#fafaf7',
  },
}

export function createAppTheme(mode) {
  const p = mode === 'dark' ? darkPalette : lightPalette

  return createTheme({
    palette: p,
    typography: {
      fontFamily: '"Geist Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400, fontSize: '2.5rem', letterSpacing: '-0.02em' },
      h2: { fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400, fontSize: '2rem', letterSpacing: '-0.02em' },
      h3: { fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400, fontSize: '1.75rem', letterSpacing: '-0.01em' },
      h4: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, fontSize: '1.05rem' },
      h6: { fontWeight: 600, fontSize: '0.95rem' },
      body1: { fontSize: '0.9rem', lineHeight: 1.55 },
      body2: { fontSize: '0.825rem', lineHeight: 1.5 },
      button: { fontWeight: 500, textTransform: 'none', letterSpacing: '0' },
      caption: { fontSize: '0.75rem', color: p.text.secondary },
    },
    shape: { borderRadius: 8 },
    shadows: [
      'none',
      '0 1px 2px rgba(0,0,0,0.04)',
      '0 2px 4px rgba(0,0,0,0.05)',
      '0 4px 8px rgba(0,0,0,0.06)',
      '0 6px 14px rgba(0,0,0,0.08)',
      ...Array(20).fill('0 10px 28px rgba(0,0,0,0.10)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: p.background.default },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': { backgroundColor: p.grey[300], borderRadius: 5 },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: p.grey[400] },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: p.background.paper,
            color: p.text.primary,
            borderBottom: `1px solid ${p.divider}`,
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            transition: 'transform 120ms cubic-bezier(.4,0,.2,1), background-color 160ms, box-shadow 160ms',
            '&:active': { transform: 'scale(0.96)' },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: `0 4px 12px ${mode === 'dark' ? 'rgba(20,184,166,0.25)' : 'rgba(13,91,77,0.18)'}` },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'transform 120ms cubic-bezier(.4,0,.2,1), background-color 160ms',
            '&:active': { transform: 'scale(0.88)' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.72rem',
            transition: 'transform 100ms cubic-bezier(.4,0,.2,1), background-color 120ms',
          },
          clickable: { '&:active': { transform: 'scale(0.94)' } },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            transition: 'transform 100ms cubic-bezier(.4,0,.2,1), background-color 140ms',
            '&:active': { transform: 'scale(0.96)' },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { transition: 'background-color 100ms ease' },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: p.text.secondary,
              backgroundColor: p.grey[50],
              borderBottom: `1px solid ${p.divider}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: { root: { borderBottom: `1px solid ${p.divider}` } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { fontSize: '0.75rem' } },
      },
    },
  })
}

export default createAppTheme('light')
